// pages/DeveloperDocs.jsx
import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  TextField,
  Snackbar,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Api as ApiIcon,
  Webhook as WebhookIcon,
  Code as CodeIcon,
  Terminal as TerminalIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

const DeveloperDocs = ({ language }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setSnackbar({
      open: true,
      message: `${label} copied to clipboard!`,
      severity: 'success'
    });
    setTimeout(() => setCopied(null), 2000);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const apiBaseUrl = 'https://qr-table.com/api/pos/v1';
  const exampleApiKey = 'rk_live_f9ac274ed6eec5b84c6e1dd1bce5a62d3fa1eb3fc1eda9c71b2b794923f53eb0';

  // Sample data
  const sampleOrderResponse = {
    id: 776,
    user_id: 20,
    total_amount: "27.50",
    status: "processing",
    payment_method: "cash_on_delivery",
    table_id: 19,
    items: [
      {
        product_id: 43,
        product_name: "Chicken Shawarma Combo",
        quantity: 1,
        price: "25.00",
        selected_size: { name: "Large", price: 3 },
        selected_extras: [{ name: "cheese", price: 3 }]
      }
    ],
    user: {
      username: "omrankanaan",
      email: "unknownperson@windowslive.com",
      user_phone: "+60 183248864"
    }
  };

  // API Endpoints
  const endpoints = [
    {
      method: 'GET',
      path: '/orders',
      description: language === 'ar' ? 'جلب الطلبات حسب الحالة' : 'Fetch orders by status',
      params: [
        { name: 'status', type: 'query', required: true, description: 'processing | delivered | canceled' }
      ],
      headers: [
        { name: 'X-API-Key', required: true, description: 'Your POS API key' }
      ],
      exampleRequest: `curl -X GET "${apiBaseUrl}/orders?status=processing" \\
  -H "X-API-Key: ${exampleApiKey}"`,
      exampleResponse: sampleOrderResponse
    },
    {
      method: 'PUT',
      path: '/orders/{orderId}/status',
      description: language === 'ar' ? 'تحديث حالة الطلب' : 'Update order status',
      params: [
        { name: 'orderId', type: 'path', required: true, description: 'Order ID' }
      ],
      body: {
        status: 'delivered | canceled'
      },
      headers: [
        { name: 'X-API-Key', required: true, description: 'Your POS API key' },
        { name: 'Content-Type', required: true, description: 'application/json' }
      ],
      exampleRequest: `curl -X PUT "${apiBaseUrl}/orders/776/status" \\
  -H "X-API-Key: ${exampleApiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"status": "delivered"}'`,
      exampleResponse: {
        success: true,
        message: "Order status updated to delivered",
        order: {
          id: 776,
          status: "delivered",
          updated_at: "2024-01-15T10:30:00.000Z"
        }
      }
    }
  ];

  // Webhook Events
  const webhookEvents = [
    {
      event: 'order.created',
      description: language === 'ar' ? 'عند إنشاء طلب جديد' : 'When a new order is created',
      icon: '🆕',
      payload: {
        event: 'order.created',
        timestamp: '2024-01-15T10:30:00.000Z',
        restaurant: { id: 2, name: 'Restaurant Name' },
        data: {
          orderId: 776,
          total: 27.50,
          status: 'processing',
          items: [{ product_id: 43, quantity: 1, price: 25 }],
          customer: { name: 'John Doe', email: 'john@example.com' }
        }
      }
    },
    {
      event: 'order.updated',
      description: language === 'ar' ? 'عند تحديث الطلب' : 'When an order is updated',
      icon: '🔄',
      payload: {
        event: 'order.updated',
        timestamp: '2024-01-15T10:30:00.000Z',
        restaurant: { id: 2, name: 'Restaurant Name' },
        data: {
          orderId: 776,
          previousStatus: 'processing',
          currentStatus: 'preparing',
          total: 27.50
        }
      }
    },
    {
      event: 'order.status_changed',
      description: language === 'ar' ? 'عند تغيير حالة الطلب' : 'When order status changes',
      icon: '📊',
      payload: {
        event: 'order.status_changed',
        timestamp: '2024-01-15T10:30:00.000Z',
        restaurant: { id: 2, name: 'Restaurant Name' },
        data: {
          orderId: 776,
          oldStatus: 'processing',
          newStatus: 'delivered',
          total: 27.50
        }
      }
    },
    {
      event: 'order.canceled',
      description: language === 'ar' ? 'عند إلغاء الطلب' : 'When an order is canceled',
      icon: '❌',
      payload: {
        event: 'order.canceled',
        timestamp: '2024-01-15T10:30:00.000Z',
        restaurant: { id: 2, name: 'Restaurant Name' },
        data: {
          orderId: 776,
          reason: 'Customer requested',
          pointsRestored: 0,
          total: 27.50
        }
      }
    },
    {
      event: 'test.webhook',
      description: language === 'ar' ? 'طلب اختبار الاتصال' : 'Test connection request',
      icon: '🧪',
      payload: {
        event: 'test.webhook',
        timestamp: '2024-01-15T10:30:00.000Z',
        restaurant: { id: 2, name: 'Restaurant Name' },
        data: { test: true, message: 'Test webhook from QR Table' }
      }
    }
  ];

  // Status Codes
  const statusCodes = [
    { code: 200, description: 'Success - Request completed successfully' },
    { code: 201, description: 'Created - Resource created successfully' },
    { code: 400, description: 'Bad Request - Invalid parameters or request body' },
    { code: 401, description: 'Unauthorized - Invalid or missing API key' },
    { code: 404, description: 'Not Found - Resource not found' },
    { code: 429, description: 'Too Many Requests - Rate limit exceeded' },
    { code: 500, description: 'Internal Server Error - Something went wrong' }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4, mb: 10, minHeight: '100vh', backgroundColor: '#0D0D0D' }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h2" sx={{ color: '#FFD700', fontWeight: 800, mb: 2 }}>
          {language === 'ar' ? 'توثيق API و Webhook' : 'API & Webhook Documentation'}
        </Typography>
        <Typography variant="body1" sx={{ color: '#B3B3B3', maxWidth: '800px', mx: 'auto' }}>
          {language === 'ar'
            ? 'قم بدمج نظام نقاط البيع الخاص بك مع منصة QR Table باستخدام واجهات برمجة التطبيقات (API) والـ Webhooks'
            : 'Integrate your POS system with QR Table platform using our APIs and Webhooks'}
        </Typography>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D' }}>
            <ApiIcon sx={{ color: '#FFD700', fontSize: 40, mb: 1 }} />
            <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 700 }}>3</Typography>
            <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'نقاط نهاية API' : 'API Endpoints'}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D' }}>
            <WebhookIcon sx={{ color: '#FFD700', fontSize: 40, mb: 1 }} />
            <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 700 }}>5</Typography>
            <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'أحداث Webhook' : 'Webhook Events'}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D' }}>
            <SecurityIcon sx={{ color: '#FFD700', fontSize: 40, mb: 1 }} />
            <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 700 }}>X-API-Key</Typography>
            <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'مصادقة API' : 'API Authentication'}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D' }}>
            <SpeedIcon sx={{ color: '#FFD700', fontSize: 40, mb: 1 }} />
            <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 700 }}>100</Typography>
            <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'طلب/دقيقة' : 'Requests/minute'}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px', overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            borderBottom: '1px solid #2D2D2D',
            '& .MuiTab-root': { color: '#B3B3B3', fontWeight: 600 },
            '& .Mui-selected': { color: '#FFD700' },
            '& .MuiTabs-indicator': { backgroundColor: '#FFD700' }
          }}
        >
          <Tab label={language === 'ar' ? 'API' : 'API'} icon={<ApiIcon />} />
          <Tab label={language === 'ar' ? 'Webhook' : 'Webhook'} icon={<WebhookIcon />} />
          <Tab label={language === 'ar' ? 'الأكواد' : 'Status Codes'} icon={<CodeIcon />} />
        </Tabs>

        {/* API Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 3, backgroundColor: 'rgba(255, 215, 0, 0.1)', color: '#FFD700', border: '1px solid #FFD700' }}>
              <Typography variant="body2">
                {language === 'ar'
                  ? '⚠️ ملاحظة: يمكن أن تتغير حالة المعالجة إلى "ملغي" أو "تم التسليم". إذا لم يتم العثور على سجل، جرب status=canceled أو status=delivered'
                  : '⚠️ Note: The processing status can change to "canceled" or "delivered". If no record found, try status=canceled or status=delivered'}
              </Typography>
            </Alert>

            {endpoints.map((endpoint, index) => (
              <Card key={index} sx={{ mb: 3, backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D', borderRadius: '12px' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={endpoint.method}
                      size="small"
                      sx={{
                        backgroundColor: endpoint.method === 'GET' ? '#4CAF50' : '#FF9800',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontFamily: 'monospace'
                      }}
                    />
                    <Typography variant="h6" sx={{ color: '#FFFFFF', fontFamily: 'monospace' }}>
                      {endpoint.path}
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(`${apiBaseUrl}${endpoint.path}`, 'URL')} sx={{ color: '#FFD700' }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 2 }}>
                    {endpoint.description}
                  </Typography>

                  {/* Headers */}
                  <Typography variant="subtitle2" sx={{ color: '#FFD700', fontWeight: 600, mt: 2 }}>Headers</Typography>
                  <Box component="ul" sx={{ color: '#B3B3B3', mb: 2 }}>
                    {endpoint.headers.map((header, i) => (
                      <li key={i}>
                        <code style={{ color: '#FFD700' }}>{header.name}</code> - {header.description} {header.required && <Chip label="Required" size="small" sx={{ ml: 1, height: 20, backgroundColor: '#F44336', color: '#FFF' }} />}
                      </li>
                    ))}
                  </Box>

                  {/* Parameters */}
                  {endpoint.params && (
                    <>
                      <Typography variant="subtitle2" sx={{ color: '#FFD700', fontWeight: 600, mt: 2 }}>Parameters</Typography>
                      <Box component="ul" sx={{ color: '#B3B3B3', mb: 2 }}>
                        {endpoint.params.map((param, i) => (
                          <li key={i}>
                            <code style={{ color: '#FFD700' }}>{param.name}</code> ({param.type}) - {param.description} {param.required && <Chip label="Required" size="small" sx={{ ml: 1, height: 20, backgroundColor: '#F44336', color: '#FFF' }} />}
                          </li>
                        ))}
                      </Box>
                    </>
                  )}

                  {/* Request Body */}
                  {endpoint.body && (
                    <>
                      <Typography variant="subtitle2" sx={{ color: '#FFD700', fontWeight: 600, mt: 2 }}>Request Body</Typography>
                      <Box sx={{ mb: 2 }}>
                        <SyntaxHighlighter
                          language="json"
                          style={vscDarkPlus}
                          customStyle={{ borderRadius: '8px', fontSize: '12px' }}
                        >
                          {JSON.stringify(endpoint.body, null, 2)}
                        </SyntaxHighlighter>
                      </Box>
                    </>
                  )}

                  {/* Example Request */}
                  <Typography variant="subtitle2" sx={{ color: '#FFD700', fontWeight: 600, mt: 2 }}>Example Request</Typography>
                  <Box sx={{ position: 'relative', mb: 2 }}>
                    <SyntaxHighlighter
                      language="bash"
                      style={vscDarkPlus}
                      customStyle={{ borderRadius: '8px', fontSize: '12px' }}
                    >
                      {endpoint.exampleRequest}
                    </SyntaxHighlighter>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(endpoint.exampleRequest, 'cURL command')}
                      sx={{ position: 'absolute', top: 8, right: 8, color: '#FFD700' }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Example Response */}
                  <Typography variant="subtitle2" sx={{ color: '#FFD700', fontWeight: 600, mt: 2 }}>Example Response</Typography>
                  <Box sx={{ position: 'relative' }}>
                    <SyntaxHighlighter
                      language="json"
                      style={vscDarkPlus}
                      customStyle={{ borderRadius: '8px', fontSize: '12px', maxHeight: '400px', overflow: 'auto' }}
                    >
                      {JSON.stringify(endpoint.exampleResponse, null, 2)}
                    </SyntaxHighlighter>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(JSON.stringify(endpoint.exampleResponse, null, 2), 'Response')}
                      sx={{ position: 'absolute', top: 8, right: 8, color: '#FFD700' }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* Webhook Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 3, backgroundColor: 'rgba(255, 215, 0, 0.1)', color: '#FFD700', border: '1px solid #FFD700' }}>
              <Typography variant="body2">
                {language === 'ar'
                  ? '📡 قم بتكوين Webhook URL في إعدادات متجرك لاستلام الإشعارات في الوقت الفعلي'
                  : '📡 Configure Webhook URL in your store settings to receive real-time notifications'}
              </Typography>
            </Alert>

            <Card sx={{ mb: 3, backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D', borderRadius: '12px' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700, mb: 2 }}>
                  {language === 'ar' ? 'كيفية الإعداد' : 'How to Setup'}
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 2 }}>
                    {language === 'ar'
                      ? '1. انتقل إلى إعدادات متجرك في لوحة التحكم'
                      : '1. Go to your store settings in the dashboard'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 2 }}>
                    {language === 'ar'
                      ? '2. أضف Webhook URL الخاص بك في حقل "Webhook URL"'
                      : '2. Add your Webhook URL in the "Webhook URL" field'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 2 }}>
                    {language === 'ar'
                      ? '3. احفظ الإعدادات - سنرسل إشعارات الطلبات تلقائياً'
                      : '3. Save settings - we will automatically send order notifications'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 2 }}>
                    {language === 'ar'
                      ? '4. استخدم زر "اختبار Webhook" للتحقق من الاتصال'
                      : '4. Use the "Test Webhook" button to verify connection'}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" sx={{ color: '#FFD700', fontWeight: 600, mb: 2 }}>Webhook Headers</Typography>
                <Box component="ul" sx={{ color: '#B3B3B3', mb: 3 }}>
                  <li><code style={{ color: '#FFD700' }}>X-Webhook-Signature</code> - توقيع أمان للتحقق من صحة الطلب</li>
                  <li><code style={{ color: '#FFD700' }}>X-Webhook-Event</code> - نوع الحدث (order.created, order.updated, etc.)</li>
                  <li><code style={{ color: '#FFD700' }}>X-Restaurant-Id</code> - معرف المطعم</li>
                  <li><code style={{ color: '#FFD700' }}>User-Agent</code> - QR-Table-Webhook/1.0</li>
                </Box>

                <Typography variant="subtitle2" sx={{ color: '#FFD700', fontWeight: 600, mb: 2 }}>Example Webhook Endpoint (Node.js)</Typography>
                <Box sx={{ position: 'relative', mb: 3 }}>
                  <SyntaxHighlighter
                    language="javascript"
                    style={vscDarkPlus}
                    customStyle={{ borderRadius: '8px', fontSize: '12px' }}
                  >
                    {`app.post('/api/webhook', (req, res) => {
  const webhookData = req.body;
  const signature = req.headers['x-webhook-signature'];
  const eventType = req.headers['x-webhook-event'];
  
  console.log(\`Received webhook: \${eventType}\`);
  console.log('Payload:', webhookData);
  
  // Process based on event type
  switch(webhookData.event) {
    case 'order.created':
      // Handle new order
      console.log(\`New order #\${webhookData.data.orderId}\`);
      break;
    case 'order.status_changed':
      // Handle status change
      console.log(\`Order #\${webhookData.data.orderId} status changed to \${webhookData.data.newStatus}\`);
      break;
  }
  
  res.json({ received: true });
});`}
                  </SyntaxHighlighter>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(`app.post('/api/webhook', (req, res) => {
  const webhookData = req.body;
  const signature = req.headers['x-webhook-signature'];
  const eventType = req.headers['x-webhook-event'];
  
  console.log(\`Received webhook: \${eventType}\`);
  console.log('Payload:', webhookData);
  
  switch(webhookData.event) {
    case 'order.created':
      console.log(\`New order #\${webhookData.data.orderId}\`);
      break;
    case 'order.status_changed':
      console.log(\`Order #\${webhookData.data.orderId} status changed to \${webhookData.data.newStatus}\`);
      break;
  }
  
  res.json({ received: true });
});`, 'Node.js code')}
                    sx={{ position: 'absolute', top: 8, right: 8, color: '#FFD700' }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>

            {/* Webhook Events */}
            <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700, mb: 2 }}>
              {language === 'ar' ? 'أحداث Webhook' : 'Webhook Events'}
            </Typography>

            {webhookEvents.map((event, index) => (
              <Accordion key={index} sx={{ mb: 2, backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#FFD700' }} />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6">{event.icon}</Typography>
                    <Typography sx={{ color: '#FFD700', fontFamily: 'monospace', fontWeight: 600 }}>{event.event}</Typography>
                    <Chip label={event.description} size="small" sx={{ backgroundColor: '#2D2D2D', color: '#B3B3B3' }} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="subtitle2" sx={{ color: '#FFD700', fontWeight: 600, mb: 1 }}>Example Payload</Typography>
                  <Box sx={{ position: 'relative' }}>
                    <SyntaxHighlighter
                      language="json"
                      style={vscDarkPlus}
                      customStyle={{ borderRadius: '8px', fontSize: '12px' }}
                    >
                      {JSON.stringify(event.payload, null, 2)}
                    </SyntaxHighlighter>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(JSON.stringify(event.payload, null, 2), 'Payload')}
                      sx={{ position: 'absolute', top: 8, right: 8, color: '#FFD700' }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {/* Status Codes Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700, mb: 2 }}>
              {language === 'ar' ? 'رموز حالة API' : 'API Status Codes'}
            </Typography>
            <Grid container spacing={2}>
              {statusCodes.map((status, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D', borderRadius: '8px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h6" sx={{ 
                        color: status.code === 200 ? '#4CAF50' : 
                               status.code === 201 ? '#4CAF50' :
                               status.code === 400 ? '#FF9800' :
                               status.code === 401 ? '#FF9800' :
                               status.code === 404 ? '#F44336' :
                               status.code === 429 ? '#FF9800' : '#F44336',
                        fontWeight: 700,
                        fontFamily: 'monospace'
                      }}>
                        {status.code}
                      </Typography>
                      <Typography sx={{ color: '#FFFFFF' }}>{status.description}</Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3, borderColor: '#2D2D2D' }} />

            <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700, mb: 2 }}>
              {language === 'ar' ? 'معدلات الطلب' : 'Rate Limits'}
            </Typography>
            <Paper sx={{ p: 3, backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D', borderRadius: '8px' }}>
              <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 1 }}>
                <strong style={{ color: '#FFD700' }}>100</strong> {language === 'ar' ? 'طلب في الدقيقة لكل مفتاح API' : 'requests per minute per API key'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 1 }}>
                <strong style={{ color: '#FFD700' }}>429 Too Many Requests</strong> {language === 'ar' ? 'سيتم إرجاعه عند تجاوز الحد' : 'will be returned when exceeded'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                <strong style={{ color: '#FFD700' }}>Retry-After</strong> {language === 'ar' ? 'الرأس يحدد وقت الانتظار' : 'header specifies wait time'}
              </Typography>
            </Paper>
          </Box>
        )}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ backgroundColor: '#0D0D0D', color: '#FFFFFF', border: `1px solid ${snackbar.severity === 'success' ? '#4CAF50' : '#FF3D00'}` }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DeveloperDocs;